import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.js';
import { Guide } from './pages/Guide.js';
import { loadConfig, isSignedIn, login, handleCallback } from './auth.js';
import { ErrorBox, Spinner } from './ui.js';
import './styles.css';

/**
 * Boot: load the runtime config, then either complete an OAuth callback or
 * ensure there is a session before rendering anything that talks to the API.
 */
function Boot() {
  // The guide needs no session, so it never waits on (or redirects to) sign-in.
  // `/guide` is the address from before the navigation was simplified.
  const publicGuide =
    (window.location.pathname === '/help' || window.location.pathname === '/guide') && !isSignedIn();
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (publicGuide) return;
    let cancelled = false;

    (async () => {
      try {
        await loadConfig();

        if (window.location.pathname === '/callback') {
          const code = new URLSearchParams(window.location.search).get('code');
          const oauthError = new URLSearchParams(window.location.search).get('error');
          if (oauthError) throw new Error(`sign-in failed: ${oauthError}`);
          if (!code) throw new Error('no authorization code in the callback');
          const returnTo = await handleCallback(code);
          window.history.replaceState({}, '', returnTo);
        } else if (!isSignedIn()) {
          // The hash too: Home's balance reminder points at a card (/trends#hsa),
          // and a sign-in on the way should not lose the place.
          await login(window.location.pathname + window.location.search + window.location.hash);
          return; // navigating away
        }

        if (!cancelled) setState('ready');
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setState('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (publicGuide) return <PublicGuide />;
  if (state === 'loading') return <Spinner what="Signing in" />;
  if (state === 'error') {
    return (
      <main>
        <ErrorBox error={error} />
        <div className="actions">
          <button className="primary" onClick={() => void login('/')}>
            Try signing in again
          </button>
        </div>
      </main>
    );
  }
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}

/** The guide for someone not signed in: no nav, just a way in. */
function PublicGuide() {
  return (
    <main>
      <Guide />
      <div className="actions">
        <button className="primary" onClick={() => void loadConfig().then(() => login('/'))}>
          Sign in
        </button>
      </div>
    </main>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Boot />
  </StrictMode>,
);
